import React, { useEffect, useId, useRef } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { LAST_UPDATED, TERMS_VERSION } from "../../constants/legal";
import LegalDocumentBody from "./LegalDocumentBody";

/**
 * Reads a legal document without leaving the signup form. `LegalPage.jsx`
 * (`/terms`, `/privacy`) stays the canonical, addressable copy — this is
 * purely a convenience so accepting doesn't cost someone everything they've
 * typed. Both render the same `content` object from `legalContent.js`, so
 * there is exactly one place the wording lives.
 *
 * Opening this does not tick the consent checkbox, and closing it does not
 * either — reading and consenting are two separate acts on purpose. Only the
 * user's own click on the checkbox is evidence of agreement (see the comment
 * in Signup.jsx); folding "closed the modal" into that would blur the one
 * thing GDPR/consumer-protection consent has to stay unambiguous about.
 */
const LegalModal = ({ content, open, onClose, showVersion = false }) => {
  const { i18n } = useTranslation();
  // Optional chaining on i18n itself, not just .language: this component is
  // mounted (closed) for the lifetime of the signup form, so it renders under
  // every test that renders Signup — including ones whose react-i18next mock
  // only stubs `t`. A crash here must not depend on every caller remembering
  // to also mock `i18n`.
  const language = i18n?.language?.startsWith("en") ? "en" : "ro";
  const doc = content[language];
  const titleId = useId();
  const closeButtonRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    // Send focus into the dialog, and back to whatever had it once the dialog
    // closes — a modal that steals focus permanently strands a keyboard user.
    const previouslyFocused = document.activeElement;
    closeButtonRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);

    // Lock page scroll while open: the signup form behind the overlay must
    // not scroll along with a long legal document.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="legal-modal-overlay fixed inset-0 z-100 flex items-center justify-center overflow-y-auto bg-ink-900/50 p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(event) => event.stopPropagation()}
        className="legal-modal-panel flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-card bg-white shadow-card-hover"
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-ink-200 px-5 py-4 sm:px-6">
          <div>
            <h2
              id={titleId}
              className="text-fluid-lg font-bold tracking-tight text-ink-900"
            >
              {doc.title}
            </h2>
            <p className="mt-1 text-xs text-ink-400">
              {language === "ro" ? "Ultima actualizare" : "Last updated"}:{" "}
              {(content.lastUpdated ?? LAST_UPDATED)[language]}
              {showVersion
                ? ` · ${language === "ro" ? "versiunea" : "version"} ${TERMS_VERSION}`
                : ""}
            </p>
          </div>

          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            aria-label={language === "ro" ? "Închide" : "Close"}
            className="legal-modal-close flex size-8 shrink-0 items-center justify-center rounded-lg text-ink-500 transition hover:bg-ink-100 hover:text-ink-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
          >
            <i className="bi bi-x text-xl" aria-hidden="true" />
          </button>
        </div>

        <div className="legal-modal-body flex-1 overflow-y-auto px-5 py-5 sm:px-6">
          <p className="text-fluid-sm leading-relaxed text-ink-600">{doc.intro}</p>
          <LegalDocumentBody sections={doc.sections} />
        </div>
      </div>
    </div>
  );
};

LegalModal.propTypes = {
  content: PropTypes.object.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  showVersion: PropTypes.bool,
};

export default LegalModal;
